import { useToast } from "@/hooks/use-toast";
import { track, type AnalyticsEvents } from "@/lib/analytics";

/**
 * ShareTarget — place/producer use slug, event uses id (uuid).
 */
export type ShareTarget =
  | {
      kind: "place";
      name: string;
      slug: string;
      municipality?: string | null;
      landskap?: string | null;
    }
  | {
      kind: "area";
      name: string;
      slug: string;
      landskap?: string | null;
    }
  | {
      kind: "producer";
      name: string;
      slug: string;
      placeName?: string | null;
      landskap?: string | null;
    }
  | {
      kind: "event";
      name: string;
      id: string; // uuid — events have no slug
      placeName?: string | null;
      landskap?: string | null;
      dateText?: string | null;
    }
  | {
      kind: "accommodation";
      name: string;
      id: string; // uuid — accommodations have no slug
    }
  | {
      kind: "inspiration";
      name: string;
      slug: string;
    };

function getIdentifier(target: ShareTarget): string {
  if (target.kind === "event") return target.id;
  if (target.kind === "accommodation") return target.id;
  return target.slug;
}

function buildShareUrl(target: ShareTarget): string {
  let origin = window.location.origin.replace(/\/+$/, "");

  // Canonical host in production
  if (origin.endsWith("farmly.se") && !origin.startsWith("https://www.")) {
    origin = origin.replace("https://", "https://www.");
  }

  return `${origin}/share/${target.kind}/${getIdentifier(target)}`;
}

async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

export function useShare() {
  const { toast } = useToast();

  const share = async (target: ShareTarget, surface = "unknown") => {
    const url = buildShareUrl(target);
    const shareData: ShareData = { url };
    const analyticsShareKind =
      target.kind as unknown as AnalyticsEvents["share_clicked"]["share_kind"];

    // 1) share_clicked — always fires
    track("share_clicked", {
      share_kind: analyticsShareKind,
      share_id_or_slug: getIdentifier(target),
      share_surface: surface,
    }, "share");

    try {
      // Use native share if available and the data is supported
      if (
        typeof navigator.share === "function" &&
        (!navigator.canShare || navigator.canShare(shareData))
      ) {
        // 2) share_native_opened
        track("share_native_opened", {
          share_kind: analyticsShareKind,
          share_surface: surface,
        }, "share");

        await navigator.share(shareData);
        return;
      }

      // 3) share_fallback_clipboard — no native share
      track("share_fallback_clipboard", {
        share_kind: analyticsShareKind,
        reason: navigator.canShare && !navigator.canShare(shareData)
          ? "cannot_share"
          : "no_navigator_share",
      }, "share");

      await copyToClipboard(url);
      toast({ title: "Länk kopierad", description: "Länken har kopierats till urklipp." });
    } catch (err) {
      if ((err as Error).name === "AbortError") return;

      try {
        // 3) share_fallback_clipboard — native share threw
        track("share_fallback_clipboard", {
          share_kind: analyticsShareKind,
          reason: "error",
        }, "share");

        await copyToClipboard(url);
        toast({ title: "Länk kopierad", description: "Länken har kopierats till urklipp." });
      } catch (clipErr) {
        // 4) share_failed — everything failed
        track("share_failed", {
          share_kind: analyticsShareKind,
          failure_reason: (clipErr as Error).message || "clipboard_failed",
        }, "share");

        toast({
          title: "Kunde inte dela",
          description: "Prova kopiera länken manuellt.",
          variant: "destructive",
        });
      }
    }
  };

  return { share };
}
